import { useCallback, useState } from 'react';
import { notify } from '@lqa-frontend/ui-system';

const DOWNLOAD_FAILED_MESSAGE = 'Unable to download';

export type BlobDownloadPayload = {
	blob: Blob;
	filename?: string;
};

export type BlobDownloadResult = Blob | BlobDownloadPayload;

export function resolveBlobDownload(
	result: BlobDownloadResult,
	fallbackFileName: string
): { blob: Blob; filename: string } {
	if (result instanceof Blob) {
		return { blob: result, filename: fallbackFileName };
	}

	const serverFilename = result.filename?.trim();
	return {
		blob: result.blob,
		filename: serverFilename || fallbackFileName,
	};
}

function triggerBlobDownload(blob: Blob, fileName: string) {
	const objectUrl = URL.createObjectURL(blob);
	const downloadLink = document.createElement('a');
	downloadLink.href = objectUrl;
	downloadLink.download = fileName;
	downloadLink.click();
	URL.revokeObjectURL(objectUrl);
}

async function tryMessageFromBlob(
	errorBody: Blob
): Promise<string | undefined> {
	try {
		const rawText = await errorBody.text();
		const parsedBody = JSON.parse(rawText) as { message?: unknown };
		if (
			typeof parsedBody.message === 'string' &&
			parsedBody.message.length > 0
		) {
			return parsedBody.message;
		}
	} catch {
		return undefined;
	}
	return undefined;
}

type AxiosLikeError = {
	message?: string;
	response?: { status?: number; data?: unknown };
};

async function resolveDownloadErrorMessage(
	unknownError: unknown
): Promise<string> {
	if (typeof unknownError !== 'object' || unknownError === null) {
		return DOWNLOAD_FAILED_MESSAGE;
	}

	const axiosLikeError = unknownError as AxiosLikeError;
	const httpStatus = axiosLikeError.response?.status;
	const responseBody = axiosLikeError.response?.data;

	if (responseBody instanceof Blob) {
		const messageFromJson = await tryMessageFromBlob(responseBody);
		if (messageFromJson) {
			return messageFromJson;
		}
		if (httpStatus != null && httpStatus >= 400) {
			return DOWNLOAD_FAILED_MESSAGE;
		}
	}

	if (
		responseBody &&
		typeof responseBody === 'object' &&
		'message' in responseBody
	) {
		const messageField = (responseBody as { message: unknown }).message;
		if (typeof messageField === 'string' && messageField.length > 0) {
			return messageField;
		}
	}

	if (httpStatus != null && httpStatus >= 400) {
		return DOWNLOAD_FAILED_MESSAGE;
	}

	const clientMessage = axiosLikeError.message;
	if (typeof clientMessage === 'string' && clientMessage.length > 0) {
		if (/^Request failed with status code \d+$/i.test(clientMessage)) {
			return DOWNLOAD_FAILED_MESSAGE;
		}
		if (clientMessage === 'Network Error') {
			return DOWNLOAD_FAILED_MESSAGE;
		}
		return clientMessage;
	}

	return DOWNLOAD_FAILED_MESSAGE;
}

export function useBlobDownload() {
	const [isDownloading, setIsDownloading] = useState<boolean>(false);

	const download = useCallback(
		async (fetchBlob: () => Promise<BlobDownloadResult>, fileName: string) => {
			setIsDownloading(true);
			try {
				const result = await fetchBlob();
				const resolved = resolveBlobDownload(result, fileName);
				triggerBlobDownload(resolved.blob, resolved.filename);
			} catch (caughtError) {
				notify.error(await resolveDownloadErrorMessage(caughtError));
			} finally {
				setIsDownloading(false);
			}
		},
		[]
	);

	return { download, isDownloading };
}
